/**
 * 위험성평가 — 근로자 의견 청취 팝업
 * RIN_BASIC과 동일 컬럼 구성, 조회·다중선택만 가능 → 선택 반영 시 세부 그리드에 필드 삽입
 */
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { setDepList, setDepCd } from '../../../../../../Reducer/SearchReducer';
import { showSnackbar } from '../../../../../../Reducer/SnackbarReducer';
import Commons from '../../../../../../Services/Common/Common';
import Global from '../../../../../../Services/Common/Global';
import { apiRequest } from '../../../../../../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../../../../../../util';
import { useSearch1 } from '../../../../../../Scripts/RiskNearMiss/RIN_BASIC/RIN_BASIC_QUERY';
import { mapGetAcsBasic02R1RowToGrid } from '../../../../../../Scripts/RiskNearMiss/RIN_BASIC/RIN_BASIC_Schema';
import useCompanyCustDep from '../../../../../../hooks/useCompanyCustDep';
import { BusinessGridCommon } from '../../../../../../Components/BusinessGridCommon';
import { getColumnDefs1 } from '../../../../../../Scripts/RiskNearMiss/RIN_BASIC/RIN_BASIC_ColumnDefs';
import { GetWorkerOpinionReadOnlyColumnDefs } from './workerOpinionReadOnlyColumnDefs';
import { MapWorkerOpinionRowsToRiskDetailFields } from '../RiskAssessment_Schema';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const noop = () => {};
const HEADER_GREEN = '#2CA900';

function WorkerOpinionModal({ open, onClose, onApplySelection }) {
  const dispatch = useDispatch();
  const searchInfo = useSelector((state) => state.SearchInfo);
  const gridRef1 = useRef();
  const rowClickData = useRef(null);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);

  const [rowData1, setRowData1] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);

  const { validateSelection: validateSelectionBase } = useCompanyCustDep({
    requireCompany: true,
    requireCust: true,
    autoFetchDep: true,
  });

  const validateSelection = useCallback(() => {
    if (!validateSelectionBase()) return false;
    return true;
  }, [validateSelectionBase]);

  const fetchDepForCust = useCallback(
    async (custCd) => {
      if (!custCd) return;
      try {
        const encryptedParams = mapParamsBySchema({
          CustCd: (_, context) => Commons.EncryptAES(custCd || '') || '',
          SearchText: (_, context) => Commons.EncryptAES('') || '',
        });
        const result = await apiRequest(
          'post',
          Global.urls.Utility.getDepLast,
          encryptedParams
        );
        const data = result?.data;
        let depList = [];
        if (data?.length > 0 && data[0]?.type !== 'Error') {
          depList = data.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        }
        dispatch(setDepList(depList));
        dispatch(setDepCd(''));
      } catch (e) {
        console.error(e);
        dispatch(setDepList([]));
        dispatch(setDepCd(''));
      }
    },
    [dispatch]
  );

  const ResetLogic = useCallback(() => {
    setRowData1([]);
    setSelectedRowData1([]);
    gridRef1?.current?.api?.deselectAll?.();
  }, []);

  const searchKey = [
    searchInfo?.custCd,
    searchInfo?.depCd,
    '',
    searchInfo?.year,
    searchInfo?.month,
    searchInfo?.gubun,
    open,
  ];

  const { refetch: Search1 } = useSearch1({
    searchKey,
    enabled: open,
    onSuccess: (rows) => {
      const mapped = (rows || [])
        .map(mapGetAcsBasic02R1RowToGrid)
        .filter(Boolean);
      setRowData1(mapped);
    },
    getSearchContext: () => ({
      search: { ...searchInfo, gongjongCd: '' },
    }),
  });

  const SearchLogic1 = useCallback(async () => {
    ResetLogic();
    if (!validateSelection()) return;
    await Search1();
  }, [Search1, validateSelection, ResetLogic]);

  const columnDefs1 = useMemo(() => {
    const stub = {
      gridRef1,
      rowData1: [],
      setRowData1: noop,
      selectedRowData1: [],
      setSelectedRowData1: noop,
      changedRowData1: [],
      setChangedRowData1: noop,
      modalToggle1: false,
      setModalToggle1: noop,
      gongjongModalToggle: false,
      setGongjongModalToggle: noop,
      custCd: '',
      rowClickData,
      HandleManageSelection1: noop,
      PopupClickEvent1: noop,
      PopupGongjongClickEvent1: noop,
      HandleGongjongSelection1: noop,
      handleRowSelection1: noop,
      ExcelDownload1: noop,
      PopupPreImageClickEvent1: noop,
      PopupPostImageClickEvent1: noop,
      SearchLogic1: noop,
      MarkedForDeletion1: noop,
      NewYnErrorMsg: noop,
      RestoreRow1: noop,
      DeleteNewRow1: noop,
      selectedRowNosRef1,
      lastCheckedIndexRef1,
    };
    return GetWorkerOpinionReadOnlyColumnDefs(getColumnDefs1(stub));
  }, []);

  const HandleSelectionChanged = useCallback((gridRef) => {
    const api = gridRef?.current?.api;
    if (!api) return;
    const rows = api.getSelectedRows?.() ?? [];
    setSelectedRowData1(rows);
  }, []);

  const HandleApply = useCallback(() => {
    if (!selectedRowData1?.length) {
      dispatch(
        showSnackbar({ message: '반영할 행을 선택하세요.', type: 'warning' })
      );
      return;
    }
    const mapped = MapWorkerOpinionRowsToRiskDetailFields(selectedRowData1);
    const ok = onApplySelection?.(mapped);
    if (ok !== false) onClose?.();
  }, [selectedRowData1, dispatch, onApplySelection, onClose]);

  useEffect(() => {
    if (!open || !searchInfo?.custCd) return;
    fetchDepForCust(searchInfo.custCd);
  }, [open, searchInfo?.custCd, fetchDepForCust]);

  useEffect(() => {
    if (!open) {
      ResetLogic();
      return;
    }
    ResetLogic();
  }, [open, ResetLogic]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: 'min(1600px, 98vw)',
          height: 'min(900px, 92vh)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="span" fontWeight={700}>
          근로자 의견 청취
        </Typography>
        <IconButton aria-label="닫기" onClick={onClose} edge="end" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          overflow: 'hidden',
        }}
      >
        <Alert
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          severity="success"
          sx={{
            mt: 1,
            mb: 1.5,
            alignItems: 'center',
            bgcolor: 'rgba(44, 169, 0, 0.12)',
            color: 'text.primary',
            border: `1px solid ${HEADER_GREEN}`,
            '& .MuiAlert-icon': { color: HEADER_GREEN },
          }}
        >
          다중 선택 후 「선택 반영」을 누르면 위험성평가 세부항목에
          유해위험요인·안전보건조치·감소대책이 입력됩니다.
        </Alert>
        <Box
          className="ag-theme-alpine"
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BusinessGridCommon
            getRowId={(params) =>
              String(
                params.data?.no ??
                  params.data?.nearMissSeq ??
                  params.data?.id ??
                  params.rowIndex
              )
            }
            editable={false}
            searchYn1="N"
            copyYn1="N"
            addYn1="N"
            saveYn1="N"
            deleteYn1="N"
            printYn1="N"
            fileUploadYn1="N"
            fileDownloadYn1="N"
            refreshYn1="Y"
            refreshClick1={() => {
              if (!validateSelection()) return;
              SearchLogic1();
            }}
            autoHeaderHeight
            stopEditingWhenCellsLoseFocus
            gridRef={gridRef1}
            columnDefs={columnDefs1}
            rowData={rowData1}
            setSelectedRowData={() => {}}
            setChangedRowData={() => {}}
            title1="의견청취 대상 목록"
            rowSelection="multiple"
            onSelectionChanged={HandleSelectionChanged}
            gridSx={{ flex: 1, minHeight: 400, height: '100%' }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}
      >
        <Button variant="contained" color="success" onClick={HandleApply}>
          선택 반영
        </Button>
        <Button variant="outlined" onClick={onClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkerOpinionModal;
